import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function NeuralCanvas() {
  const canvasRef = useRef();
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    const NODE_COUNT = 60;
    const nodes = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 2 + 1, pulse: Math.random() * Math.PI * 2,
    }));
    const particles = Array.from({ length: 50 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.15, vy: (Math.random() - 0.5) * 0.15,
      r: Math.random() * 1.2 + 0.3, alpha: Math.random() * 0.4 + 0.1,
    }));
    let t = 0;
    const draw = () => {
      ctx.fillStyle = 'rgba(8,14,25,0.15)';
      ctx.fillRect(0, 0, W, H);
      t += 0.006;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 150) {
            const alpha = (1 - dist/150) * 0.2;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(59,130,246,${alpha * (Math.sin(t*2+nodes[i].pulse)*0.5+0.5)})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
        n.pulse += 0.02;
        const g = Math.sin(n.pulse) * 0.4 + 0.6;
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r*4);
        grad.addColorStop(0, `rgba(99,179,237,${0.8*g})`);
        grad.addColorStop(1, 'rgba(59,130,246,0)');
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r*4, 0, Math.PI*2); ctx.fillStyle = grad; ctx.fill();
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI*2); ctx.fillStyle = `rgba(147,210,255,${g})`; ctx.fill();
      });
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(200,230,255,${p.alpha})`; ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', background:'#080e19' }}/>;
}

function InteractiveChart({ data, dates, color, unit='' }) {
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef();
  if (!data || data.length === 0) return null;
  const H = 80;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = e.clientX - rect.left;
    const ratio = relX / rect.width;
    const idx = Math.round(ratio * (data.length - 1));
    const clamped = Math.max(0, Math.min(idx, data.length - 1));
    setTooltip({ ratio: relX / rect.width, v: data[clamped], i: clamped });
  };

  // Рисуем через процентные позиции
  const pts = data.map((v, i) => ({
    px: (i / (data.length - 1)) * 100,
    py: H - ((v - min) / range) * (H - 10) - 5,
  }));
  const pathD = pts.map((p, i) => `${i===0?'M':'L'} ${p.px}% ${p.py}`).join(' ');

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return String(d);
    return `${String(dt.getDate()).padStart(2,'0')}.${String(dt.getMonth()+1).padStart(2,'0')}`;
  };

  return (
    <div ref={containerRef} style={{ position:'relative', width:'100%', height:H }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}>
      <svg width="100%" height={H} style={{ display:'block', overflow:'visible' }}>
        <defs>
          <linearGradient id={`g${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
            <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
          </linearGradient>
        </defs>
        {pts.map((p, i) => i > 0 && (
          <line key={i}
            x1={`${pts[i-1].px}%`} y1={pts[i-1].py}
            x2={`${p.px}%`} y2={p.py}
            stroke={color} strokeWidth={2.5} strokeLinecap="round"/>
        ))}
        {pts.map((p, i) => (
          <circle key={i} cx={`${p.px}%`} cy={p.py} r={tooltip?.i===i ? 5 : 2.5}
            fill={tooltip?.i===i ? color : color} opacity={tooltip?.i===i ? 1 : 0.6}
            stroke={tooltip?.i===i ? '#080e19' : 'none'} strokeWidth={2}/>
        ))}
        {tooltip && (
          <line
            x1={`${tooltip.ratio*100}%`} y1={0}
            x2={`${tooltip.ratio*100}%`} y2={H}
            stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.4}/>
        )}
      </svg>
      {tooltip && (
        <div style={{
          position:'absolute', bottom:'100%',
          left:`${Math.min(Math.max(tooltip.ratio*100, 8), 88)}%`,
          transform:'translateX(-50%)', marginBottom:6,
          background:'rgba(15,23,42,0.96)', backdropFilter:'blur(10px)',
          border:`1px solid ${color}50`, borderRadius:8,
          padding:'5px 10px', pointerEvents:'none', whiteSpace:'nowrap',
          fontSize:11, color:'#e2e8f0',
          boxShadow:'0 4px 16px rgba(0,0,0,0.4)',
        }}>
          <div style={{ color:'rgba(148,163,184,0.7)', marginBottom:2 }}>{fmtDate(dates?.[tooltip.i])}</div>
          <div style={{ fontWeight:700, color }}>{tooltip.v.toLocaleString('ru-RU')}{unit}</div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, subtitle, value, unit, data, dates, color, chartUnit, onClick, delay=0 }) {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), delay); return () => clearTimeout(t); }, []);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, minWidth: 280,
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        border: `1px solid ${hovered ? color+'70' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 24,
        padding: '28px 24px 20px',
        cursor: 'default',
        transform: mounted
          ? (hovered ? 'scale(1.08) translateY(-10px)' : 'scale(1) translateY(0)')
          : 'scale(0.93) translateY(16px)',
        opacity: mounted ? 1 : 0,
        transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
        boxShadow: hovered
          ? `0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px ${color}40, 0 0 40px ${color}18`
          : '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)' }}/>
      <div style={{ position:'absolute', top:-30, right:-30, width:180, height:180, background:`radial-gradient(circle,${color}15 0%,transparent 65%)`, pointerEvents:'none' }}/>

      <div style={{ marginBottom:16, position:'relative' }}>
        <div style={{ fontSize:10, color:'rgba(148,163,184,0.5)', letterSpacing:'2.5px', textTransform:'uppercase', fontWeight:600, marginBottom:8 }}>{title}</div>
        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
          <span style={{ fontSize:36, fontWeight:800, color:'#f1f5f9', letterSpacing:'-1.5px', lineHeight:1 }}>{value}</span>
          {unit && <span style={{ fontSize:14, color:'rgba(148,163,184,0.5)', fontWeight:500 }}>{unit}</span>}
        </div>
        {subtitle && <div style={{ fontSize:12, color:`${color}bb`, marginTop:6, fontWeight:500 }}>{subtitle}</div>}
      </div>

      <InteractiveChart data={data} dates={dates} color={color} unit={chartUnit}/>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 50);
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 29);
      const fmt = d => d.toISOString().split('T')[0];
      const r = await api.get(`/monitoring/data?from=${fmt(from)}&to=${fmt(to)}`);
      const articles = r.data.articles || [];
      const dates = r.data.dates || [];

      const ordersSumByDay = dates.map(date =>
        articles.reduce((sum, a) => sum + (Number(a.daily?.[date]?.orders_sum) || 0), 0)
      );
      const spendByDay = dates.map(date =>
        articles.reduce((sum, a) => sum + (Number(a.daily?.[date]?.spend_all) || 0), 0)
      );
      const buyoutData = [[], [], [], []];
      articles.forEach(a => {
        [a.buyoutW1, a.buyoutW2, a.buyoutW3, a.buyoutW4].forEach((w, i) => {
          if (w !== null && w !== undefined) buyoutData[i].push(w);
        });
      });
      const buyoutByWeek = buyoutData.map(week =>
        week.length > 0 ? Math.round(week.reduce((s,v)=>s+v,0)/week.length) : 0
      );
      const totalOrdersSum = ordersSumByDay.reduce((s,v)=>s+v,0);
      const totalSpend = spendByDay.reduce((s,v)=>s+v,0);
      const avgBuyout = buyoutByWeek.filter(v=>v>0).length > 0
        ? Math.round(buyoutByWeek.filter(v=>v>0).reduce((s,v)=>s+v,0)/buyoutByWeek.filter(v=>v>0).length) : 0;

setStats({ ordersSumByDay, spendByDay, buyoutByWeek, totalOrdersSum, totalSpend, avgBuyout, dates });
    } catch(e) { console.error(e); }
  };

  const fmt = v => v >= 1000000
    ? `${(v/1000000).toFixed(1)}М`
    : v >= 1000 ? `${Math.round(v/1000)}К` : String(v);

  return (
    <div style={{ minHeight:'100vh', position:'relative', overflow:'hidden', background:'#080e19', margin:0 }}>
      <NeuralCanvas/>
      <div style={{ position:'absolute', inset:0, background:'rgba(8,14,25,0.35)', pointerEvents:'none', zIndex:1 }}/>

      <div style={{ position:'relative', zIndex:10, minHeight:'100vh', padding:'0 5%', display:'flex', flexDirection:'column', justifyContent:'center' }}>



        {/* Слоган */}
        <div style={{ marginBottom:40, opacity:mounted?1:0, transform:mounted?'translateY(0)':'translateY(-12px)', transition:'all 0.7s 0.1s' }}>
          <div style={{ fontSize:12, color:'rgba(148,163,184,0.45)', letterSpacing:'4px', textTransform:'uppercase', fontWeight:500 }}>
            следи · управляй · расти
          </div>
        </div>

        {/* Карточки */}
        <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          <StatCard
            title="Заказы за 30 дней"
            value={stats ? fmt(stats.totalOrdersSum) : '—'}
            unit="₽"
            subtitle={stats ? `≈ ${fmt(Math.round(stats.totalOrdersSum/30))} ₽/день` : 'загрузка...'}
            data={stats?.ordersSumByDay}
            dates={stats?.dates}
            color="#3b82f6"
            chartUnit=" ₽"
            delay={150}
            
          />
          <StatCard
            title="Затраты на рекламу"
            value={stats ? fmt(stats.totalSpend) : '—'}
            unit="₽"
            subtitle={stats ? `≈ ${fmt(Math.round(stats.totalSpend/30))} ₽/день` : 'загрузка...'}
            data={stats?.spendByDay}
            dates={stats?.dates}
            color="#f59e0b"
            chartUnit=" ₽"
            delay={250}
            
          />
          <StatCard
            title="Средний % выкупа"
            value={stats ? `${stats.avgBuyout}` : '—'}
            unit="%"
            subtitle={stats ? `W1·W2·W3·W4: ${stats.buyoutByWeek.join('·')}%` : 'загрузка...'}
            data={stats?.buyoutByWeek}
            dates={['W1','W2','W3','W4']}
            color="#10b981"
            chartUnit="%"
            delay={350}
            
          />
        </div>
      </div>
    </div>
  );
}
